import type { NormalizedRow } from "@/types";

/**
 * Convert rows back to CSV text with RFC 4180 escaping. Used for the
 * cleaned-data and per-category downloads (all generated in-browser).
 */

/** Canonical output column order (mirrors the Python processor's keep-columns + margins). */
export const OUTPUT_COLUMNS = [
  "Product ID",
  "Description",
  "Price",
  "Cost",
  "Manufacturer",
  "Mfg Part #",
  "Vendor",
  "Vendor SKU",
  "On Hand",
  "Type",
  "Category",
  "Subcategory",
  "Last Update",
  "Margin_$",
  "Margin_%",
] as const;

const FIELD_BY_COLUMN: Record<string, keyof NormalizedRow> = {
  "Product ID": "product_id",
  Description: "description",
  Price: "price",
  Cost: "cost",
  Manufacturer: "manufacturer",
  "Mfg Part #": "mfg_part",
  Vendor: "vendor",
  "Vendor SKU": "vendor_sku",
  "On Hand": "on_hand",
  Type: "type",
  Category: "category",
  Subcategory: "subcategory",
  "Last Update": "last_update",
  "Margin_$": "margin_dollar",
  "Margin_%": "margin_percent",
};

/** Escape a single field per RFC 4180 (quote when it contains `"`, `,`, CR, or LF). */
export function escapeCsvField(value: unknown): string {
  const text = value === null || value === undefined ? "" : String(value);
  if (/[",\r\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

/** Serialize rows to an RFC 4180 CSV string (CRLF line endings, trailing newline). */
export function toCsv(
  rows: ReadonlyArray<Record<string, unknown>>,
  columns: readonly string[],
): string {
  const lines = [columns.map((column) => escapeCsvField(column)).join(",")];
  for (const row of rows) {
    lines.push(columns.map((column) => escapeCsvField(row[column])).join(","));
  }
  return lines.join("\r\n") + "\r\n";
}

/** Serialize normalized rows to CSV using the canonical {@link OUTPUT_COLUMNS}. */
export function normalizedRowsToCsv(
  rows: readonly NormalizedRow[],
  columns: readonly string[] = OUTPUT_COLUMNS,
): string {
  const records = rows.map((row) => {
    const record: Record<string, unknown> = {};
    for (const column of columns) {
      const field = FIELD_BY_COLUMN[column];
      record[column] = field === undefined ? "" : row[field];
    }
    return record;
  });
  return toCsv(records, columns);
}

const INVALID_FILENAME_CHARS = /[/\\:*?"<>|]/g;
const WINDOWS_RESERVED = new Set([
  "CON", "PRN", "AUX", "NUL",
  ...Array.from({ length: 9 }, (_, i) => `COM${i + 1}`),
  ...Array.from({ length: 9 }, (_, i) => `LPT${i + 1}`),
]);

/**
 * Return a filesystem-safe version of `name` (mirrors the Python
 * `sanitize_filename`): replaces characters illegal on Windows/POSIX and control
 * characters, trims trailing dots/spaces, guards Windows reserved device names,
 * and caps length. Used for per-category download filenames.
 */
export function sanitizeFilename(name: string, replacement = "_", maxLength = 255): string {
  let sanitized = "";
  for (const char of name.replace(INVALID_FILENAME_CHARS, replacement)) {
    sanitized += /[\p{Cc}\p{Cf}]/u.test(char) ? replacement : char;
  }
  sanitized = sanitized.trim().replace(/[ .]+$/, "").trim();
  if (sanitized === "") return replacement;
  const stem = sanitized.split(".")[0] ?? "";
  if (WINDOWS_RESERVED.has(stem.toUpperCase())) {
    sanitized = `${replacement}${sanitized}`;
  }
  return sanitized.slice(0, maxLength);
}
